import { apiClient } from '../lib/apiClient';
import { config } from '../lib/config';

export interface Checkpoint {
    id: number;
    checkpointId: number;
    startBlock: number;
    endBlock: number;
    aptosTxHash: string;
    aptosBlockNumber: number;
    transactionsSynced: boolean;
    createdAt: string;
    updatedAt: string;
}

interface ApiCheckpointsResponse {
    success: boolean;
    data: {
        data: Checkpoint[];
        total?: number;
        perPage?: number;
        currentPage?: number;
        lastPage?: number;
        nextPage?: number | null;
        prevPage?: number | null;
    };
    message?: string;
}

export interface GetCheckpointsResponse {
    checkpoints: Checkpoint[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

export interface VerifyTxnResponse {
    success: boolean;
    message: string;
    data: {
        verified: boolean;
        besuBlock: number;
        proof: Array<{
            position: string;
            data: string;
        }>;
        aptosMerkleRoot: string;
        calculatedMerkleRoot: string;
    };
}

export const checkpointsService = {
    async getCheckpoints(page: number, limit: number): Promise<GetCheckpointsResponse> {
        const qs = new URLSearchParams({ page: String(page), limit: String(limit) }).toString();
        const baseUrl = config.besuApiBaseUrl;
        if (!baseUrl) {
            throw new Error('BESU API base URL not configured. Please set REACT_APP_BESU_API_URL in your .env and restart the dev server.');
        }
        const res = await apiClient<ApiCheckpointsResponse>(`api/checkpoints?${qs}`, { method: 'GET', baseUrl });

        const d = res.data || ({} as ApiCheckpointsResponse['data']);

        const total = typeof d.total === 'number' ? d.total : (d.data?.length ?? 0);
        const perPage = typeof d.perPage === 'number' ? d.perPage : limit;
        const currentPage = typeof d.currentPage === 'number' ? d.currentPage : page;
        const lastPage = typeof d.lastPage === 'number' ? d.lastPage : Math.max(1, Math.ceil(total / perPage));

        return {
            checkpoints: d.data || [],
            totalCount: total,
            page: currentPage,
            pageSize: perPage,
            totalPages: lastPage,
        };
    },

    // Transactions under a checkpoint
    async getCheckpointTxns(params: { checkpointId: number; startBlock?: number; endBlock?: number; txHash?: string; blockNumber?: number; page: number; limit: number }): Promise<{
        txns: { id: number; checkpointId: number; blockNumber: number; txHash: string; createdAt?: string; updatedAt?: string }[];
        totalCount: number;
        page: number;
        pageSize: number;
        totalPages: number;
    }> {
        const baseUrl = config.besuApiBaseUrl;
        if (!baseUrl) {
            throw new Error('BESU API base URL not configured. Please set REACT_APP_BESU_API_URL in your .env and restart the dev server.');
        }

        const { page, limit, checkpointId, startBlock, endBlock, txHash, blockNumber } = params;
        const qs = new URLSearchParams({ page: String(page), limit: String(limit) }).toString();

        type ApiCheckpointTxnsResponse = {
            success: boolean;
            data: {
                data: { id: number; checkpointId: number; blockNumber: number; txHash: string; createdAt?: string; updatedAt?: string }[];
                current_page?: number;
                per_page?: number;
                last_page?: number;
                total?: number;
            };
            message?: string;
        };

        const body: any = { checkpointId };
        if (typeof startBlock === 'number') body.startBlock = startBlock;
        if (typeof endBlock === 'number') body.endBlock = endBlock;
        if (txHash) body.txHash = txHash;
        if (typeof blockNumber === 'number') body.blockNumber = blockNumber;

        const res = await apiClient<ApiCheckpointTxnsResponse>(`api/checkpoints/txns?${qs}`, {
            method: 'POST',
            baseUrl,
            body,
        });

        const d = res.data || ({} as ApiCheckpointTxnsResponse['data']);
        const total = typeof d.total === 'number' ? d.total : (d.data?.length ?? 0);
        const perPage = typeof d.per_page === 'number' ? d.per_page : limit;
        const currentPage = typeof d.current_page === 'number' ? d.current_page : page;
        const lastPage = typeof d.last_page === 'number' ? d.last_page : Math.max(1, Math.ceil(total / perPage));

        return {
            txns: d.data || [],
            totalCount: total,
            page: currentPage,
            pageSize: perPage,
            totalPages: lastPage,
        };
    },
    async verifyTxn(txHash: string): Promise<VerifyTxnResponse> {
        const baseUrl = config.besuApiBaseUrl;
        if (!baseUrl) {
            throw new Error('BESU API base URL not configured. Please set REACT_APP_BESU_API_URL in your .env and restart the dev server.');
        }
        return apiClient<VerifyTxnResponse>(`/api/verification/${txHash}`, { method: 'POST', baseUrl });
    },
};
